import type { Shape } from './shapes';
import { drawShape, textBoxLines } from './render';
import { NATURAL_H, NATURAL_W } from './naturalSpace';
import { pinVisualBounds } from './geometry';

// The drawing half of a flattened annotation, apart from the browser's image
// loading and data URLs in `raster.ts`, so the server can draw with it on a
// Node canvas (TOADO-716). Nothing here touches `document` or `Image`; the
// only DOM names it uses are canvas types, which a Node consumer maps to its
// own canvas (see apps/api/src/types/annotation-canvas.d.ts).

export { bugSpriteReady, provideBugSprite } from './bugSpriteImage';
export { NATURAL_H, NATURAL_W } from './naturalSpace';

/** Where an image of this size sits in the drawing space: fitted, its aspect kept, letterboxed. */
export function fitInDrawingSpace(width: number, height: number): { x: number; y: number; w: number; h: number } {
  // A picture with no size (a broken upload) fills the space rather than
  // dividing by zero into a box of NaN.
  if (!(width > 0 && height > 0)) return { x: 0, y: 0, w: NATURAL_W, h: NATURAL_H };
  const srcAR = width / height;
  const dstAR = NATURAL_W / NATURAL_H;
  if (srcAR > dstAR) {
    const h = NATURAL_W / srcAR;
    return { x: 0, y: (NATURAL_H - h) / 2, w: NATURAL_W, h };
  }
  const w = NATURAL_H * srcAR;
  return { x: (NATURAL_W - w) / 2, y: 0, w, h: NATURAL_H };
}

type Rect = { x: number; y: number; w: number; h: number };

/** How far past its own coordinates a shape's drawing can reach: line widths, arrowheads, shadows. */
const SHAPE_REACH = 16;
/** The most a blur shape's radius can be, in drawing-space pixels. */
const BLUR_RADIUS_MAX = 64;

function shapeBox(s: Shape, measure?: CanvasRenderingContext2D): Rect | null {
  switch (s.kind) {
    case 'stroke': {
      if (!s.points.length) return null;
      // One pass rather than Math.min(...points): spreading a long stroke
      // overflows the stack (z.ai extension review, 17 Oct).
      let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
      for (const [px, py] of s.points) {
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
      const reach = s.width / 2;
      return { x: minX - reach, y: minY - reach, w: maxX - minX + 2 * reach, h: maxY - minY + 2 * reach };
    }
    case 'arrow':
      return { x: Math.min(s.x1, s.x2), y: Math.min(s.y1, s.y2), w: Math.abs(s.x2 - s.x1), h: Math.abs(s.y2 - s.y1) };
    case 'pin': {
      const box = pinVisualBounds(s);
      return { x: box.left, y: box.top, w: box.width, h: box.height };
    }
    case 'textBox': {
      const box = { x: Math.min(s.x, s.x + s.w), y: Math.min(s.y, s.y + s.h), w: Math.abs(s.w), h: Math.abs(s.h) };
      // A word wider than the box runs past its right edge (`textBoxLines`).
      let right = box.x + box.w;
      if (measure) for (const line of textBoxLines(measure, s)) right = Math.max(right, line.x + line.width);
      return { ...box, w: right - box.x };
    }
    case 'circle': case 'redact': case 'blur':
      return { x: Math.min(s.x, s.x + s.w), y: Math.min(s.y, s.y + s.h), w: Math.abs(s.w), h: Math.abs(s.h) };
    default:
      return null;
  }
}

/**
 * The part of the drawing space a flattened picture needs: the image as
 * fitted, grown to take in every shape, so a note written in the letterbox
 * beside a wide picture is kept, and an empty letterbox is left off.
 * `measure` is any 2D context with the text font loaded: without it, text
 * running past its box is not counted.
 */
export function drawnArea(image: { width: number; height: number }, shapes: Shape[], measure?: CanvasRenderingContext2D): Rect {
  const fit = fitInDrawingSpace(image.width, image.height);
  let left = fit.x;
  let top = fit.y;
  let right = fit.x + fit.w;
  let bottom = fit.y + fit.h;
  for (const shape of shapes) {
    const box = shapeBox(shape, measure);
    if (!box || ![box.x, box.y, box.w, box.h].every(Number.isFinite)) continue;
    left = Math.min(left, box.x - SHAPE_REACH);
    top = Math.min(top, box.y - SHAPE_REACH);
    right = Math.max(right, box.x + box.w + SHAPE_REACH);
    bottom = Math.max(bottom, box.y + box.h + SHAPE_REACH);
  }
  left = Math.max(0, left);
  top = Math.max(0, top);
  right = Math.min(NATURAL_W, right);
  bottom = Math.min(NATURAL_H, bottom);
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * Draws an image and its shapes into a context whose coordinates are the
 * drawing space (NATURAL_W x NATURAL_H): the image fitted and letterboxed,
 * each blur as the image blurred under it, then every other shape. The one
 * routine for a flattened annotation, in the browser and on the server that
 * sends pictures to Jira (TOADO-716), so the two cannot draw differently.
 *
 * `deviceScale` is how far the context is scaled from the drawing space: a
 * canvas filter's blur radius is in the canvas's own pixels, not the
 * transformed ones, so it is scaled by hand to blur as much at any size.
 * The caller waits for the bug sprite first, if any pin needs it.
 */
export function drawAnnotated(
  ctx: CanvasRenderingContext2D,
  image: { source: CanvasImageSource; width: number; height: number } | null,
  shapes: Shape[],
  deviceScale = 1,
): void {
  if (image) {
    const { x, y, w, h } = fitInDrawingSpace(image.width, image.height);
    ctx.drawImage(image.source, x, y, w, h);
    // Apply blur to underlying image regions. Each blur shape clips
    // to its rect, sets ctx.filter, and re-draws the image — the
    // result is the image-but-blurred only inside the rect. Done
    // BEFORE drawShape so the placeholder fill doesn't end up in
    // the export PNG (drawShape's blur branch is for the live
    // canvas; on export we render the actual blur instead).
    for (const s of shapes) {
      if (s.kind !== 'blur') continue;
      const sx = Math.min(s.x, s.x + s.w);
      const sy = Math.min(s.y, s.y + s.h);
      const sw = Math.abs(s.w);
      const sh = Math.abs(s.h);
      if (sw < 2 || sh < 2) continue;
      ctx.save();
      ctx.beginPath();
      ctx.rect(sx, sy, sw, sh);
      ctx.clip();
      // Cleared first: where the image is transparent, the blurred copy is
      // too, and drawn over the sharp one it would leave that showing.
      ctx.clearRect(sx, sy, sw, sh);
      // Bounded and numeric before it reaches the filter string: shapes come
      // from stored data, not only from the editor.
      const radius = typeof s.radius === 'number' && Number.isFinite(s.radius) ? Math.min(Math.max(s.radius, 0), BLUR_RADIUS_MAX) : 8;
      ctx.filter = `blur(${radius * deviceScale}px)`;
      ctx.drawImage(image.source, x, y, w, h);
      ctx.restore();
    }
  }
  // Skip blur shapes here — they were rendered above against the image.
  // Their drawShape branch is the live-canvas placeholder fill and would
  // produce visible artifacts in the export PNG.
  for (const s of shapes) {
    if (s.kind === 'blur') continue;
    drawShape(ctx, s, false);
  }
}
